import classNames from 'clsx';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import {
  fbPixelCustomEvents,
  fbPixelStandardEvents,
  fbTrack,
} from 'src/client/analytics/facebook/fbTrack';
import { FAULTCODE_USERNAME_EXISTS } from 'src/common/constants/faultCodes';
import { NEXT, SIGN_UP, SIGN_UP_FAILED } from 'src/common/constants/localizations/auth';
import {
  EMAIL_ADDRESS,
  EMAIL_ALREADY_EXISTS,
  EMAIL_REQUIRED,
  NAME,
  PASSWORD,
  PASSWORD_REQUIRED,
} from 'src/common/constants/localizations/shared';
import { SOURCE_ONAIR } from 'src/common/constants/sourceQueryParams';
import { getPartnerParams } from 'src/common/utils/auth/partnerAuthFlowHelpers';
import { logClientError, logSignUpClick } from '../../actions/logging';
import account from '../../constants/analytics/categoryActionLabel/account';
import { recaptchaActions } from '../../constants/auth/recaptcha';
import {
  AUTH_EMAIL_SIGNUP_ENABLED,
  AUTH_EMAIL_SIGNUP_RECAPTCHA_ENABLED,
} from '../../constants/experiments/auth';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import { selectExperiment } from '../../selectors/config';
import { getRecaptchaToken } from '../../utils/auth/recaptcha';
import {
  baseError,
  composeValidators,
  isRequired,
  isValidEmail,
  isValidFullName,
  isValidPassword,
} from '../../utils/form/validation';
import InkButton from '../shared/button/InkButton';
import PremiumGoldButton from '../shared/button/PremiumGoldButton';
import ShowPasswordButton from '../shared/button/ShowPasswordButton';
import FormInput from '../shared/form/FormInput';
import { RadioButton } from '../shared/form/RadioButton';
import TopLevelErrorContainer from '../shared/form/TopLevelErrorContainer';
import { inputCheckmarkSvgProps } from '../shared/styles/partnerAuthSharedStyles';
import Checkmark from '../shared/svgIcons/LegacyCheckmark';
import checkFieldsForValidInput from '../utils/checkFieldsForValidInput';
import authCss from './auth.module.scss';
import AuthFlowPromoOfferFinePrint from './shared/AuthFlowPromoOfferFinePrint';
import AuthSectionDivider from './shared/AuthSectionDivider';
import GeneralNotices from './shared/GeneralNotices';
import OauthButtons from './shared/OauthButtons';
import SecondaryContent from './shared/SecondaryContent';
import css from './sign-up-view.module.scss';

const fieldOrder = ['name', 'email', 'password'];

const svgProps = {
  ...inputCheckmarkSvgProps,
  className: authCss.checkmark,
};

/* eslint-disable no-unused-vars */
// exported for testing
export const RadioButtonWrapper = ({ input = {}, isPartner, ...rest }) => (
  <RadioButton
    {...input}
    className={classNames(css.signUpOption, {
      [css.isPartnerFlow]: isPartner,
    })}
    labelClassName={classNames(css.signUpOptionLabel, {
      [css.isPartnerFlow]: isPartner,
    })}
    spanClassName={css.customSpanClassName}
    defaultChecked={input.checked}
    handleClick={(evt) => {
      input.onChange(evt.target.value);
    }}
    {...rest}
  />
);
/* eslint-enable no-unused-vars */

class SignUpView extends Component {
  static propTypes = {
    actions: PropTypes.shape({
      createAccount: PropTypes.func.isRequired,
      createAccountWithOauth: PropTypes.func.isRequired,
      trackSignUpPage: PropTypes.func.isRequired,
    }).isRequired,
    logSignUpClick: PropTypes.func.isRequired,
    logClientError: PropTypes.func.isRequired,
    handleChangeToSignIn: PropTypes.func.isRequired,
    viewTypeData: PropTypes.object.isRequired,
    failAuthNotice: PropTypes.string,
    queryProduct: PropTypes.object,
    isDialog: PropTypes.bool,
    isPartner: PropTypes.bool,
    source: PropTypes.string,
    isPaymentFlow: PropTypes.bool,
    isEmailSignUpEnabled: PropTypes.bool.isRequired,
    isRecaptchaV3Enabled: PropTypes.bool.isRequired,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);

    this.state = {
      showPassword: false,
    };

    this.handleOAuthSignUp = this.handleOAuthSignUp.bind(this);
    this.handleShowPasswordClick = this.handleShowPasswordClick.bind(this);
  }

  componentDidMount() {
    fbTrack(fbPixelCustomEvents.SignUpPageVisit);
    this.props.actions.trackSignUpPage();
  }

  handleOAuthSignUp(...args) {
    const [providerName, data, source, params] = args;
    const { actions, viewTypeData } = this.props;

    actions
      .createAccountWithOauth(providerName, data, source, params, { viewTypeData })
      .then(() => {
        fbTrack(fbPixelStandardEvents.CompleteRegistration);
      });
  }

  onSubmit = async (values, form) => {
    const {
      actions,
      isPartner,
      source,
      viewTypeData,
      isRecaptchaV3Enabled,
      logClientError: onRecaptchaError,
    } = this.props;
    const { location } = this.context;
    const { name, password, email } = values;
    const recaptchaV3Token = isRecaptchaV3Enabled
      ? await getRecaptchaToken(recaptchaActions.emailSignUp, onRecaptchaError)
      : null;

    fbTrack(fbPixelStandardEvents.CompleteRegistration);
    this.props.logSignUpClick(account.labels.email, source);

    try {
      await actions.createAccount({
        data: {
          username: email?.trim?.(),
          password,
          firstName: name,
          source,
          recaptchaV3Token,
        },
        params: getPartnerParams(isPartner, location?.query),
        viewTypeData,
      });

      form.restart();
    } catch (error) {
      switch (error.body?.faultCode) {
        case FAULTCODE_USERNAME_EXISTS:
          return baseError(EMAIL_ALREADY_EXISTS, [email]);
        default:
          return baseError(SIGN_UP_FAILED);
      }
    }
  };

  handleShowPasswordClick(event) {
    event.preventDefault();
    this.setState({ showPassword: !this.state.showPassword });
  }

  renderPersonalInfoFields(validFieldsMap, values) {
    const { showPassword } = this.state;
    const { isPartner } = this.props;
    const { getLocalizedText } = this.context;

    if (isPartner) {
      return (
        <div className={authCss.section}>
          <div className={classNames(authCss.fieldsSection, authCss.isPartnerFlow)}>
            <div className="row">
              <div className="col-12">
                <Field
                  component={FormInput}
                  hideInlineError
                  rootClassName={classNames(
                    authCss.inputField,
                    'emailField',
                    authCss.isPartnerFlow,
                  )}
                  dataTestId="email"
                  name="email"
                  id="suEmail"
                  autoCorrect="off"
                  autoCapitalize="none"
                  label={getLocalizedText(EMAIL_ADDRESS)}
                  required
                  localizeErrorMessages
                  validate={composeValidators(isRequired(EMAIL_REQUIRED), isValidEmail)}
                />
                {validFieldsMap?.email && <Checkmark {...svgProps} />}
              </div>
            </div>
          </div>
          <div className={classNames(authCss.fieldsSection, authCss.isPartnerFlow)}>
            <div className="row">
              <div className="col-12">
                <Field
                  component={FormInput}
                  hideInlineError
                  rootClassName={classNames(
                    authCss.inputField,
                    'passwordField',
                    authCss.isPartnerFlow,
                  )}
                  dataTestId="password"
                  type="password"
                  name="password"
                  id="suPassword"
                  label={getLocalizedText(PASSWORD)}
                  required
                  localizeErrorMessages
                  validate={composeValidators(isRequired(PASSWORD_REQUIRED), isValidPassword)}
                />
                {validFieldsMap?.password && <Checkmark {...svgProps} />}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={classNames(authCss.section, {
          [authCss.noSpacing]: !isPartner,
        })}
      >
        <div className={authCss.fieldsSection}>
          <div className="row">
            <div className="col-12">
              <Field
                component={FormInput}
                hideInlineError
                rootClassName={classNames(authCss.inputField, 'nameField')}
                dataTestId="name"
                name="name"
                id="suName"
                validate={isValidFullName}
                placeholder={getLocalizedText(NAME)}
              />
            </div>
            <div className="col-12">
              <Field
                component={FormInput}
                hideInlineError
                rootClassName={classNames(authCss.inputField, 'emailField')}
                dataTestId="email"
                placeholder={getLocalizedText(EMAIL_ADDRESS)}
                name="email"
                id="suEmail"
                autoCorrect="off"
                autoCapitalize="none"
                required
                validate={composeValidators(isRequired(EMAIL_REQUIRED), isValidEmail)}
              />
            </div>
          </div>
        </div>
        <div className={authCss.fieldsSection}>
          <div className="row">
            <div className={classNames('col-12', authCss.inputWrapper)}>
              <Field
                component={FormInput}
                hideInlineError
                rootClassName={classNames(
                  authCss.inputField,
                  'passwordField',
                  authCss.alongWithIcon,
                )}
                placeholder={getLocalizedText(PASSWORD)}
                dataTestId="password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="suPassword"
                required
                validate={composeValidators(isRequired(PASSWORD_REQUIRED), isValidPassword)}
              />
              {!isPartner && values?.password && (
                <ShowPasswordButton
                  showPassword={showPassword}
                  onClick={this.handleShowPasswordClick}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  getSignUpButton = ({ pristine, submitting, submitSucceeded, hasValidationErrors }) => {
    const { isPartner, isPaymentFlow } = this.props;
    const { getLocalizedText } = this.context;

    const SignUpButtonElement = isPartner ? PremiumGoldButton : InkButton;
    const isSubmitDisabled = pristine || submitting || submitSucceeded || hasValidationErrors;

    return (
      <SignUpButtonElement
        id="signUpButton"
        type="submit"
        label={getLocalizedText(isPaymentFlow ? NEXT : SIGN_UP)}
        isDisabled={isSubmitDisabled}
        className={classNames(authCss.authButton, {
          [authCss.isPartnerFlow]: isPartner,
        })}
      />
    );
  };

  renderTopLevelErrorContainer(pristine, touched, submitErrors, errors) {
    const { isPartner } = this.props;

    return (
      <TopLevelErrorContainer
        className={classNames(authCss.largeSection, {
          [authCss.noSpacing]: !isPartner,
        })}
        customErrorListClass={classNames(authCss.customErrorListClass, {
          [authCss.isPartnerFlow]: isPartner,
        })}
        errorTextClassName={classNames(authCss.customErrorTextClass, {
          [authCss.isPartnerFlow]: isPartner,
        })}
        errorMessages={!pristine || touched ? { ...errors, ...submitErrors } : {}}
        isPartner={isPartner}
        fieldOrder={fieldOrder}
        touchedFields={touched}
      />
    );
  }

  render() {
    const {
      handleChangeToSignIn,
      failAuthNotice,
      queryProduct,
      isDialog,
      isPartner,
      source,
      isPaymentFlow,
      isEmailSignUpEnabled,
    } = this.props;

    const { location } = this.context;
    const isOnAirFlow = source === SOURCE_ONAIR;
    const showEmailSignUp = isPartner || isEmailSignUpEnabled || isOnAirFlow;

    return (
      <Form onSubmit={this.onSubmit}>
        {({
          handleSubmit,
          errors,
          pristine,
          touched,
          hasValidationErrors,
          submitting,
          submitSucceeded,
          submitErrors,
          values,
        }) => {
          const validFieldsMap = isPartner
            ? checkFieldsForValidInput(touched, {
                ...errors,
                ...submitErrors,
              })
            : null;

          return (
            <form onSubmit={handleSubmit}>
              <div data-testid="signUp" className={authCss.containerRestrictions}>
                {!isOnAirFlow && (
                  <OauthButtons
                    actionHandler={this.handleOAuthSignUp}
                    loggingHandler={this.props.logSignUpClick}
                    isDialog={isDialog}
                    isSignUp
                    isPaymentFlow={isPaymentFlow}
                    isPartner={isPartner}
                    isEmailSignUpEnabled={isEmailSignUpEnabled}
                    source={source}
                    partnerParams={getPartnerParams(isPartner, location?.query)}
                  />
                )}
                {isPartner &&
                  this.renderTopLevelErrorContainer(pristine, touched, submitErrors, errors)}
                <AuthSectionDivider isPartner={isPartner} />
                <GeneralNotices key="signInGeneralNotices" failAuthNotice={failAuthNotice} />
                {!isPartner &&
                  this.renderTopLevelErrorContainer(pristine, touched, submitErrors, errors)}
                {showEmailSignUp && this.renderPersonalInfoFields(validFieldsMap, values)}
                <div className={authCss.section}>
                  <div className="row">
                    <div className="col-12">
                      {showEmailSignUp &&
                        this.getSignUpButton({
                          pristine,
                          hasValidationErrors,
                          submitting,
                          submitSucceeded,
                        })}
                      <SecondaryContent
                        key="signUpSecondaryContent"
                        isPartnerFlow={isPartner}
                        handler={handleChangeToSignIn}
                      />
                      {isPartner && !!queryProduct?.freeTrialLength && (
                        <AuthFlowPromoOfferFinePrint containerClassName={authCss.finePrintStyle} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          );
        }}
      </Form>
    );
  }
}

export function mapStateToProps(state) {
  return {
    isEmailSignUpEnabled: selectExperiment(state, AUTH_EMAIL_SIGNUP_ENABLED, true),
    isRecaptchaV3Enabled: selectExperiment(state, AUTH_EMAIL_SIGNUP_RECAPTCHA_ENABLED, false),
  };
}

const mapDispatchToProps = {
  logSignUpClick,
  logClientError,
};

export default connect(mapStateToProps, mapDispatchToProps)(SignUpView);
